import { useEffect, useState } from "react";
import { Keyboard, Platform } from "react-native";

/**
 * Tracks soft-keyboard height with a small cushion so a bottom-anchored
 * element can lift above it.
 *
 * Native: iOS exposes `keyboardWillShow/Hide` which fire ahead of the actual
 * keyboard frame — clean lift animation. Android exposes `keyboardDidShow/Hide`
 * which fires after — acceptable snap.
 *
 * Web (the mobile.taskshealth.com PWA): React Native's `Keyboard` module never
 * emits and `KeyboardAvoidingView` is a no-op, so an on-screen keyboard silently
 * covers anything anchored to the bottom of a Modal (which renders as a
 * `position: fixed` overlay sized to the *layout* viewport — that does NOT shrink
 * when the keyboard opens). We read `window.visualViewport` instead: how much the
 * visual viewport shrank from the layout viewport is the keyboard's height.
 *
 * Returns 0 when no keyboard, or keyboard height + cushion when one is shown.
 */
export function useKeyboardOffset(cushion = 12): number {
    const [offset, setOffset] = useState(0);
    useEffect(() => {
        if (Platform.OS === "web") {
            const vv = typeof window !== "undefined" ? window.visualViewport : null;
            if (!vv) {
                return;
            }
            const update = () => {
                // Keyboard height = how much the visual viewport shrank from the
                // layout viewport, minus any scroll offset the browser applied.
                const overlap = window.innerHeight - vv.height - vv.offsetTop;
                setOffset(overlap > 1 ? Math.round(overlap) + cushion : 0);
            };
            update();
            vv.addEventListener("resize", update);
            vv.addEventListener("scroll", update);
            return () => {
                vv.removeEventListener("resize", update);
                vv.removeEventListener("scroll", update);
            };
        }

        const showEvt = Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow";
        const hideEvt = Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide";
        const showSub = Keyboard.addListener(showEvt, (e) => {
            setOffset((e?.endCoordinates?.height ?? 0) + cushion);
        });
        const hideSub = Keyboard.addListener(hideEvt, () => setOffset(0));
        return () => {
            showSub.remove();
            hideSub.remove();
        };
    }, [cushion]);
    return offset;
}
